import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import NowName from '../components/shared/NowName';
import { FormModal, Field, FilePicker, inputCls, btnGhost } from '../components/shared/FormKit';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import { detailLabel, detailValue } from '../components/workflow/constants';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// Attendance dispute — detail page (docs/modules/workflow-ld-dispute.md).
// Approval IS the correction: attendance in, absence out.
const CHANGE_LABELS = {
  sessionTitle: 'Session',
  sessionDate: 'Session Date',
  message: 'What They Say',
  remarks: 'Remarks',
  // 附件區叫 Evidence——退件意見與 History 跟著這麼叫。
  files: 'Evidence',
};

const fmtChangeValue = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(s) ? formatDate(s) : s;
};

const WorkflowDisputeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [absenceRecord, setAbsenceRecord] = useState(null);
  const [attendanceRecord, setAttendanceRecord] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [approving, setApproving] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/ld/dispute-requests/${id}`),
        fetchJSON(`/api/ld/dispute-requests/${id}/events`),
      ]);
      setRequest(detail.request);
      setAbsenceRecord(detail.absenceRecord || null);
      setAttendanceRecord(detail.attendanceRecord || null);
      setCanReview(detail.canReview);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/dispute-requests/${id}/approve`, { method: 'POST', body: JSON.stringify({}) });
      setApproving(false);
      setNotice('Dispute approved — the attendance has been recorded and the requester notified.');
      await load();
    } catch (e) {
      setError(e.message);
      setApproving(false);
    }
    finally { setActing(false); }
  };

  const resubmit = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/dispute-requests/${id}/resubmit`, { method: 'POST' });
      setNotice('Dispute re-submitted — reviewers have been notified.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const hasAwaitingInfo = request && request.status === 'awaiting_info';
  const pending = request && request.status === 'requested';

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (canReview && pending);
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: CHANGE_LABELS,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/dispute"
      backLabel="Attendance Dispute"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="Attendance Dispute"
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          {attendanceRecord && pending && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
              <span>
                {request.staffNameNow || request.staffName} is already recorded as having attended this session — there is nothing to
                correct, so reject this dispute.
              </span>
            </div>
          )}
          {/* What approval will actually do to the records — stated before the
              click, because it deletes a row as well as creating one. */}
          {absenceRecord && pending && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-start gap-2">
              <span>
                An absence is on record for this session
                {absenceRecord.reasonCategory ? ` (${absenceRecord.reasonCategory})` : ''} — approving this dispute
                removes it, along with any evidence filed with it.
              </span>
            </div>
          )}

          <StatusActionsCard
            showDecider
            sides={[{
              key: 'main',
              label: null,
              status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onApprove={canReview && pending ? () => setApproving(true) : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify && request.status !== 'approved' ? () => setModifying(true) : null}
            onResubmit={canModify && hasAwaitingInfo ? resubmit : null}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
          >
            <div>
              <p className={detailLabel}>Session</p>
              <p className={detailValue}>{request.sessionTitle}</p>
              {request.status === 'approved' && (
                <p className="text-xs text-green-600 mt-1">Attendance recorded on the training record</p>
              )}
            </div>
          </StatusActionsCard>

          <ChangesSinceReturnCard
            events={events}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtChangeValue}
          />

          <SectionCard title="Dispute Details">
            <DetailGrid>
              <DetailItem label="Staff"><NowName value={request.staffNameNow || request.staffName} now={request.staffNameNow} /></DetailItem>
              <DetailItem label="Session" field="sessionTitle">{request.sessionTitle}</DetailItem>
              <DetailItem label="Session Date" field="sessionDate">{request.sessionDate ? formatDate(request.sessionDate) : '—'}</DetailItem>
              <DetailItem label="What They Say" field="message">{request.message}</DetailItem>
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          <SectionCard title="Evidence">
            <AttachmentsList
              files={request.files || []}
              downloadHref={(f) => `${API_BASE_URL}/api/ld/dispute-requests/${request.id}/files/${f.id}`}
              badgeFor={() => ['Evidence', 'bg-blue-50 text-blue-700']}
            />
            <FieldCommentAnchor field="files" label="Evidence" />
          </SectionCard>

          <HistoryCard
            events={events}
            actionLabels={{ record_created: ['Attendance record created', 'bg-green-50 text-green-700'] }}
            changeLabels={CHANGE_LABELS}
            fmtChangeValue={fmtChangeValue}
          />

          {approving && (
            <FormModal
              title={`Approve ${request.requestNo}`}
              size="md"
              onClose={() => setApproving(false)}
              footer={(
                <>
                  <button type="button" onClick={() => setApproving(false)} className={btnGhost}>Cancel</button>
                  <button
                    type="button"
                    onClick={approve}
                    disabled={acting}
                    className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {acting ? 'Approving…' : 'Approve'}
                  </button>
                </>
              )}
            >
              <p className="text-sm text-slate-700">
                Approving records <span className="font-semibold">{request.staffNameNow || request.staffName}</span> as having attended{' '}
                <span className="font-semibold">{request.sessionTitle}</span>, with the session&apos;s own training hours
                {absenceRecord ? ', and removes the absence record for that session' : ''}.
              </p>
              {attendanceRecord && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  Already recorded as attended — approving will fail. Return the dispute for changes instead.
                </div>
              )}
            </FormModal>
          )}
          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/ld/dispute-requests/${request.id}/reject`, {
                  method: 'POST',
                  body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <DisputeModifyModal
              request={request}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(hasAwaitingInfo
                  ? 'Changes saved. Press Re-submit to send the dispute back for review.'
                  : 'Changes saved.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the dispute and its evidence. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/ld/dispute-requests/${request.id}`, { method: 'DELETE' });
                navigate('/workflow/dispute');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

const DisputeModifyModal = ({ request, events = [], onClose, onDone }) => {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(
    request.sessionId != null ? String(request.sessionId) : ''
  );
  const [message, setMessage] = useState(request.message || '');
  const [remarks, setRemarks] = useState(request.remarks || '');
  const [files, setFiles] = useState([]);
  const { removeIds, toggle, appendTo } = useRemovableFiles();
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    fetchJSON('/api/ld/dispute-requests/options')
      .then((d) => {
        if (!active) return;
        const list = d.sessions || [];
        // Keep the disputed session selectable even if it aged out of the
        // 90-day window while the dispute sat in the queue.
        if (request.sessionId != null && !list.some(s => String(s.id) === String(request.sessionId))) {
          list.push({ id: request.sessionId, courseTitle: request.sessionTitle, date: request.sessionDate });
        }
        setSessions(list);
      })
      .catch(() => { if (active) setSessions([]); });
    return () => { active = false; };
  }, [request.sessionId, request.sessionTitle, request.sessionDate]);

  // The one field this modal requires, on the `field=` key the return
  // comments anchor to (the session keeps whatever was declared).
  const gaps = useMemo(
    () => (message.trim() ? [] : [{ key: 'message', label: 'Why the Record Is Wrong' }]),
    [message],
  );
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      if (sessionId) form.set('sessionId', sessionId);
      form.set('message', message.trim());
      form.set('remarks', remarks);
      files.forEach(f => form.append('files', f));
      appendTo(form);
      await fetchJSON(`/api/ld/dispute-requests/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="md"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      )}
    >
      <ReturnCommentsBanner events={events} />
      {(error || validation.error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error || validation.error}</div>
      )}
      <FormValidationProvider value={validation.providerValue}>
        <Field label="Training Session" field="sessionTitle">
          <select value={sessionId} onChange={e => setSessionId(e.target.value)} className={inputCls}>
            {sessions.map(s => (
              <option key={s.id} value={s.id}>
                {s.courseTitle}{s.date ? ` — ${formatDate(s.date)}` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Why the Record Is Wrong" field="message" required>
          <textarea rows="4" value={message} onChange={e => setMessage(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Remarks" field="remarks">
          <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
        </Field>
        <EditableAttachments
          files={request.files || []}
          removeIds={removeIds}
          onToggle={toggle}
          downloadHref={(f) => `${API_BASE_URL}/api/ld/dispute-requests/${request.id}/files/${f.id}`}
          badgeFor={() => ['Evidence', 'bg-blue-50 text-blue-700']}
          label="Current Evidence"
        />
        <FieldCommentInline field="files" />
        <Field label="Add Evidence">
          <FilePicker
            multiple
            kind="docsAndImages"
            onChange={e => setFiles(Array.from(e.target.files || []))}
          />
        </Field>
      </FormValidationProvider>
    </FormModal>
  );
};

export default WorkflowDisputeDetail;
